import React from "react";
import { Radio, Search, Heart, History, Star, X } from "lucide-react";
import { usePlayer } from "@/context/PlayerContext";
import { useAppState } from "@/context/AppState";
import { useI18n } from "@/context/I18nContext";

const NavItem = ({ icon: Icon, label, active, onClick, testid, count }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className={`w-full flex items-center gap-3 px-3 py-2 rounded-md font-mono-tech text-xs uppercase tracking-widest transition ${
      active ? "bg-[var(--voix-cyan)]/10 text-cyan-300" : "text-slate-400 hover:text-white hover:bg-white/5"
    }`}
  >
    <Icon size={14} strokeWidth={1.75} />
    <span className="flex-1 text-left">{label}</span>
    {count != null && <span className="text-[10px] text-slate-500">{count}</span>}
  </button>
);

export default function Sidebar() {
  const { favorites, history } = usePlayer();
  const { view, setView, filters, setFilters, searchQuery, setSearchQuery } = useAppState();
  const { t } = useI18n();

  const clearFilters = () => setFilters({ tag: null, language: null, country: null, mood: null, decade: null });

  return (
    <aside
      data-testid="sidebar"
      className="hidden lg:flex flex-col shrink-0 w-[260px] h-screen sticky top-0 border-r border-[var(--voix-border)] bg-[var(--voix-panel)] z-20"
    >
      <div className="px-5 pt-6 pb-4 flex items-center gap-2.5">
        <div className="w-9 h-9 rounded-md bg-cyan-500/20 border border-cyan-500/40 flex items-center justify-center">
          <Radio size={16} className="text-cyan-300" />
        </div>
        <div>
          <div className="font-heading text-xl font-black tracking-tight">VOIX</div>
          <div className="font-mono-tech text-[9px] uppercase text-slate-500 tracking-[0.25em]">{t("nav.tagline")}</div>
        </div>
      </div>

      <div className="px-4 pb-3">
        <label className="relative block">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            data-testid="sidebar-search"
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setView("search"); }}
            placeholder={t("sidebar.search")}
            className="w-full bg-black/50 border border-[var(--voix-border)] rounded-md pl-8 pr-3 py-2 text-sm font-body outline-none focus:border-cyan-500/60"
          />
        </label>
      </div>

      <div className="px-3 space-y-1">
        <NavItem icon={Star} label={t("nav.top")} active={view === "top"} onClick={() => setView("top")} testid="nav-top" />
        <NavItem icon={Heart} label={t("nav.favorites")} active={view === "favorites"} onClick={() => setView("favorites")} testid="nav-favorites" count={favorites.length} />
        <NavItem icon={History} label={t("nav.recent")} active={view === "history"} onClick={() => setView("history")} testid="nav-history" count={history.length} />
      </div>

      {(filters.country || filters.tag || filters.language || filters.mood || filters.decade) && (
        <div className="px-4 pt-5">
          <div className="flex items-center justify-between mb-2">
            <span className="font-mono-tech text-[10px] uppercase tracking-widest text-slate-500">{t("sidebar.active_filters")}</span>
            <button data-testid="clear-filters" onClick={clearFilters} className="text-[10px] text-slate-500 hover:text-pink-400 font-mono-tech uppercase">{t("sidebar.clear")}</button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {filters.mood && <Chip label={filters.mood} color="cyan" onClose={() => setFilters({ ...filters, mood: null, tag: null })} />}
            {filters.decade && <Chip label={filters.decade} color="pink" onClose={() => setFilters({ ...filters, decade: null, tag: null })} />}
            {filters.country && <Chip label={filters.country} color="cyan" onClose={() => setFilters({ ...filters, country: null })} />}
            {filters.tag && !filters.mood && !filters.decade && <Chip label={filters.tag} color="pink" onClose={() => setFilters({ ...filters, tag: null })} />}
            {filters.language && <Chip label={filters.language} color="yellow" onClose={() => setFilters({ ...filters, language: null })} />}
          </div>
        </div>
      )}

      <div className="mt-auto p-4 border-t border-[var(--voix-border)]">
        <div className="font-mono-tech text-[9px] uppercase tracking-widest text-slate-600">{t("sidebar.powered_by")}</div>
        <div className="font-mono-tech text-[9px] text-slate-700 mt-1">{t("sidebar.footer_note").toUpperCase()}</div>
      </div>
    </aside>
  );
}

function Chip({ label, color, onClose }) {
  const colorMap = {
    cyan: "border-cyan-500 text-cyan-300 bg-cyan-500/10",
    pink: "border-pink-500 text-pink-300 bg-pink-500/10",
    yellow: "border-yellow-500 text-yellow-300 bg-yellow-500/10",
  };
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-mono-tech uppercase tracking-wider px-2 py-1 rounded border ${colorMap[color]}`}>
      {label}
      <button onClick={onClose} className="hover:text-white"><X size={10} /></button>
    </span>
  );
}
